/**
 * Gerenciamento de API Cliente
 * Interface para configurar documentação de API por cliente e gerenciar tokens
 */
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  FileText,
  Code,
  Globe,
  Key,
  Plus,
  Trash2,
  Copy,
  CheckCircle2,
  XCircle,
  ExternalLink,
} from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { listClients, Client } from "@/services/clientsService";
import {
  listClientApiTokens,
  createClientApiToken,
  revokeClientApiToken,
  ClientApiToken,
} from "@/services/clientApiTokenService";
import { toast } from "sonner";
import { usePermissions } from "@/hooks/usePermissions";

interface ClientAPIManagementProps {
  onBack: () => void;
}

export const ClientAPIManagement: React.FC<ClientAPIManagementProps> = ({
  onBack,
}) => {
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [tokens, setTokens] = useState<ClientApiToken[]>([]);
  const [loading, setLoading] = useState(false);
  const [tokensLoading, setTokensLoading] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
  const [tokenToRevoke, setTokenToRevoke] = useState<ClientApiToken | null>(
    null
  );
  const { isAdminOrSupport } = usePermissions();
  const [newToken, setNewToken] = useState<string | null>(null);
  const [newTokenName, setNewTokenName] = useState("");
  const [newTokenDescription, setNewTokenDescription] = useState("");
  const [copiedToken, setCopiedToken] = useState<string | null>(null);

  // Normalizar CLIENT_API_URL removendo /client-api se presente para evitar duplicação
  const getBaseUrl = () => {
    const url = import.meta.env.VITE_CLIENT_API_URL || "http://localhost:5000";
    // Remove /client-api do final se presente
    return url.replace(/\/client-api\/?$/, "");
  };

  const CLIENT_API_URL = getBaseUrl();

  useEffect(() => {
    loadClients();
  }, [isAdminOrSupport]);

  useEffect(() => {
    if (selectedClient) {
      loadTokens();
    }
  }, [selectedClient]);

  const loadClients = async () => {
    setLoading(true);
    try {
      const response = await listClients({ limit: 100 });
      if (response.success && response.data) {
        const clientsList = response.data.clients || [];
        // Adicionar cliente SandBox no início da lista apenas para Admin e Suporte
        const allClients = isAdminOrSupport
          ? [
              {
                id: "sandbox",
                identifier: "sandbox",
                name: "SandBox",
                email: "",
                company: "SandBox",
                description:
                  "Ambiente de testes antes de liberar para o cliente",
                status_id: 1,
                status_name: "Ativo",
                status: {
                  id: 1,
                  name: "Ativo",
                  description: "Cliente ativo",
                  is_active: true,
                },
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
                products: [],
                product_count: 0,
              } as Client,
              ...clientsList,
            ]
          : clientsList;
        setClients(allClients);
      }
    } catch (error) {
      toast.error("Erro ao carregar clientes");
    } finally {
      setLoading(false);
    }
  };

  const loadTokens = async () => {
    if (!selectedClient) return;

    setTokensLoading(true);
    try {
      // Se for SandBox, usar "sandbox" como ID
      const clientId =
        selectedClient.id === "sandbox" ? "sandbox" : selectedClient.id;
      const response = await listClientApiTokens(clientId);
      if (response.success && response.data) {
        setTokens(response.data);
      }
    } catch (error) {
      toast.error("Erro ao carregar tokens");
    } finally {
      setTokensLoading(false);
    }
  };

  const handleCreateToken = async () => {
    if (!selectedClient) return;

    try {
      // Se for SandBox, usar "sandbox" como ID
      const clientId =
        selectedClient.id === "sandbox" ? "sandbox" : selectedClient.id;
      const tokenData: { name?: string; description?: string } = {};
      if (newTokenName) {
        tokenData.name = newTokenName;
      }
      if (newTokenDescription) {
        tokenData.description = newTokenDescription;
      }
      const response = await createClientApiToken(clientId, tokenData);

      if (response.success && response.data) {
        const token = response.data.token || "";
        setNewToken(token);
        setNewTokenName("");
        setNewTokenDescription("");
        await loadTokens();
        toast.success(
          "Token criado com sucesso! O token anterior foi revogado automaticamente.",
          {
            duration: 5000,
          }
        );
      } else {
        toast.error(response.error?.message || "Erro ao criar token");
      }
    } catch (error) {
      toast.error("Erro ao criar token");
    }
  };

  const handleRevokeToken = async () => {
    if (!tokenToRevoke) return;

    try {
      const response = await revokeClientApiToken(tokenToRevoke.id);

      if (response.success) {
        toast.success("Token revogado com sucesso");
        setRevokeDialogOpen(false);
        setTokenToRevoke(null);
        await loadTokens();
      } else {
        toast.error(response.error?.message || "Erro ao revogar token");
      }
    } catch (error) {
      toast.error("Erro ao revogar token");
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedToken(text);
    toast.success("Copiado!");
    setTimeout(() => setCopiedToken(null), 2000);
  };

  const getApiDocsUrl = (identifier: string) => {
    // CLIENT_API_URL já está normalizado (sem /client-api)
    // Adicionamos /client-api aqui porque o ALB roteia /client-api/* para o serviço
    return `${CLIENT_API_URL}/client-api/${identifier}/api-docs/#/`;
  };

  const getHealthUrl = (identifier: string) => {
    // CLIENT_API_URL já está normalizado (sem /client-api)
    // Adicionamos /client-api aqui porque o ALB roteia /client-api/* para o serviço
    return `${CLIENT_API_URL}/client-api/${identifier}/health`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted">
      <PageHeader
        icon={Code}
        title="API Cliente"
        description="Configure a documentação de API personalizada por cliente"
        onBack={onBack}
      />

      <main className="container mx-auto p-6">
        <div className="max-w-6xl mx-auto space-y-6">
          {/* Informações */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Configuração de API por Cliente
              </CardTitle>
              <CardDescription>
                Configure documentação de API personalizada para cada cliente.
                Esta funcionalidade permite que cada cliente tenha sua própria
                documentação de acesso aos especialistas.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Seleção de Cliente */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    Selecionar Cliente
                  </label>
                  {loading ? (
                    <div className="text-sm text-muted-foreground">
                      Carregando clientes...
                    </div>
                  ) : clients.length === 0 ? (
                    <div className="text-sm text-muted-foreground">
                      Nenhum cliente encontrado. Cadastre clientes primeiro.
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      {clients.map((client) => (
                        <Card
                          key={client.id}
                          className={`cursor-pointer transition-all ${
                            selectedClient?.id === client.id
                              ? "border-primary bg-primary/5"
                              : "hover:border-primary/50"
                          }`}
                          onClick={() => setSelectedClient(client)}
                        >
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-medium">{client.company}</p>
                                {client.identifier && (
                                  <p className="text-xs text-muted-foreground mt-1">
                                    ID: {client.identifier}
                                  </p>
                                )}
                              </div>
                              {selectedClient?.id === client.id && (
                                <Badge variant="default">Selecionado</Badge>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </div>

                {/* Detalhes do Cliente Selecionado */}
                {selectedClient && (
                  <div className="space-y-4 mt-6">
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-base flex items-center gap-2">
                          <Globe className="h-4 w-4" />
                          Documentação da API
                        </CardTitle>
                        <CardDescription>
                          Acesse a documentação Swagger personalizada para este
                          cliente
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        {selectedClient.identifier ? (
                          <>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="outline"
                                onClick={() =>
                                  window.open(
                                    getApiDocsUrl(selectedClient.identifier),
                                    "_blank"
                                  )
                                }
                              >
                                <ExternalLink className="h-4 w-4 mr-2" />
                                Abrir Documentação Swagger
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() =>
                                  copyToClipboard(
                                    getApiDocsUrl(selectedClient.identifier)
                                  )
                                }
                              >
                                <Copy className="h-4 w-4" />
                              </Button>
                            </div>
                            <div className="text-sm text-muted-foreground">
                              <p>
                                URL:{" "}
                                <code className="bg-muted px-2 py-1 rounded">
                                  {getApiDocsUrl(selectedClient.identifier)}
                                </code>
                              </p>
                              <p className="mt-2">
                                Health Check:{" "}
                                <code className="bg-muted px-2 py-1 rounded">
                                  {getHealthUrl(selectedClient.identifier)}
                                </code>
                              </p>
                            </div>
                          </>
                        ) : selectedClient.id === "sandbox" ? (
                          <div className="text-sm text-muted-foreground">
                            <p>
                              URL:{" "}
                              <code className="bg-muted px-2 py-1 rounded">
                                {getApiDocsUrl("sandbox")}
                              </code>
                            </p>
                            <p className="mt-2">
                              Health Check:{" "}
                              <code className="bg-muted px-2 py-1 rounded">
                                {getHealthUrl("sandbox")}
                              </code>
                            </p>
                          </div>
                        ) : (
                          <div className="text-sm text-amber-600">
                            Cliente não possui identificador. Configure um
                            identificador para habilitar a documentação.
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    {/* Gerenciamento de Tokens */}
                    <Card>
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <div>
                            <CardTitle className="text-base flex items-center gap-2">
                              <Key className="h-4 w-4" />
                              Tokens de API
                            </CardTitle>
                            <CardDescription>
                              Gerencie os tokens de autenticação para este
                              cliente
                            </CardDescription>
                          </div>
                          <Button
                            size="sm"
                            onClick={() => {
                              setNewTokenName("");
                              setNewTokenDescription("");
                              setCreateDialogOpen(true);
                            }}
                          >
                            <Plus className="h-4 w-4 mr-2" />
                            Criar Token
                          </Button>
                        </div>
                      </CardHeader>
                      <CardContent>
                        {tokensLoading ? (
                          <div className="text-sm text-muted-foreground">
                            Carregando tokens...
                          </div>
                        ) : tokens.length === 0 ? (
                          <div className="text-sm text-muted-foreground text-center py-4">
                            Nenhum token criado. Clique em "Criar Token" para
                            criar um novo.
                          </div>
                        ) : (
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Nome</TableHead>
                                <TableHead>Descrição</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead>Criado em</TableHead>
                                <TableHead>Revogado em</TableHead>
                                <TableHead>Ações</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {tokens.map((token) => (
                                <TableRow key={token.id}>
                                  <TableCell>
                                    {token.name || "Sem nome"}
                                  </TableCell>
                                  <TableCell>
                                    {token.description || "-"}
                                  </TableCell>
                                  <TableCell>
                                    {token.is_active && !token.revoked_at ? (
                                      <Badge
                                        variant="default"
                                        className="bg-green-500"
                                      >
                                        <CheckCircle2 className="h-3 w-3 mr-1" />
                                        Ativo
                                      </Badge>
                                    ) : (
                                      <Badge variant="destructive">
                                        <XCircle className="h-3 w-3 mr-1" />
                                        Revogado
                                      </Badge>
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    {new Date(
                                      token.created_at
                                    ).toLocaleDateString("pt-BR", {
                                      day: "2-digit",
                                      month: "2-digit",
                                      year: "numeric",
                                    })}
                                  </TableCell>
                                  <TableCell>
                                    {token.revoked_at ? (
                                      new Date(
                                        token.revoked_at
                                      ).toLocaleDateString("pt-BR", {
                                        day: "2-digit",
                                        month: "2-digit",
                                        year: "numeric",
                                      })
                                    ) : (
                                      <span className="text-muted-foreground">
                                        -
                                      </span>
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    {token.is_active && !token.revoked_at && (
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => {
                                          setTokenToRevoke(token);
                                          setRevokeDialogOpen(true);
                                        }}
                                      >
                                        <Trash2 className="h-4 w-4 text-destructive" />
                                      </Button>
                                    )}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Dialog de Criar Token */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Criar Novo Token</DialogTitle>
            <DialogDescription>
              Crie um novo token de API para o cliente. O token será exibido
              apenas uma vez. O token anterior será automaticamente revogado.
            </DialogDescription>
          </DialogHeader>
          {newToken ? (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-sm font-medium text-green-800 mb-2">
                  Token criado com sucesso!
                </p>
                <p className="text-xs text-green-700 mb-3">
                  Copie este token agora. Ele não será exibido novamente.
                </p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-white border border-green-200 rounded px-3 py-2 text-sm break-all font-mono">
                    {newToken}
                  </code>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copyToClipboard(newToken)}
                    title="Copiar token"
                  >
                    {copiedToken === newToken ? (
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
                <div className="mt-2 text-xs text-green-700">
                  <strong>Importante:</strong> Este token será exibido apenas
                  uma vez. Certifique-se de copiá-lo e armazená-lo em local
                  seguro.
                </div>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => {
                    copyToClipboard(newToken);
                  }}
                >
                  <Copy className="h-4 w-4 mr-2" />
                  Copiar Token
                </Button>
                <Button
                  onClick={() => {
                    setNewToken(null);
                    setCreateDialogOpen(false);
                  }}
                >
                  Fechar
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="token-name">Nome (opcional)</Label>
                  <Input
                    id="token-name"
                    placeholder="Ex: Token de Produção"
                    value={newTokenName}
                    onChange={(e) => setNewTokenName(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="token-description">
                    Descrição (opcional)
                  </Label>
                  <Textarea
                    id="token-description"
                    placeholder="Descrição do token"
                    value={newTokenDescription}
                    onChange={(e) => setNewTokenDescription(e.target.value)}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setCreateDialogOpen(false)}
                >
                  Cancelar
                </Button>
                <Button onClick={handleCreateToken}>Criar Token</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog de Revogar Token */}
      <AlertDialog open={revokeDialogOpen} onOpenChange={setRevokeDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revogar Token</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja revogar este token? Esta ação não pode ser
              desfeita. O token deixará de funcionar imediatamente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRevokeToken}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Revogar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
